import React from 'react';

import { Box, Chip, Stack, Typography } from '@mui/material';

import type { CorridorCampaignQualification, CorridorPriceValidation } from '~/types/corridorOpportunity';

const SIGNAL_LABELS = {
  lePaidFareP25: 'LE P25',
};

function money(value: number, currency: string) {
  return new Intl.NumberFormat('en-AU', { style: 'currency', currency }).format(value);
}

export default function QualificationSummary({
  qualification,
  validation,
}: {
  qualification: CorridorCampaignQualification;
  validation?: CorridorPriceValidation | null;
}) {
  return (
    <Stack gap={2} sx={{ border: '1px solid', borderColor: 'divider', borderRadius: 2, p: 2 }}>
      <Stack direction={{ xs: 'column', sm: 'row' }} justifyContent="space-between" gap={1}>
        <Box>
          <Typography variant="subtitle1" fontWeight={700}>
            Recorded automation decision
          </Typography>
          <Typography variant="body2" color="text.secondary">
            Evaluated {new Date(qualification.evaluatedAt).toLocaleString('en-AU')} using a customer fare of{' '}
            {money(qualification.currentFare, qualification.currency)}
            {validation
              ? ` for ${validation.carrier ?? 'the selected airline'}, ${validation.departureDate}–${validation.returnDate}, returned by ${validation.provider}`
              : ''}
            .
          </Typography>
        </Box>
        <Chip
          color={qualification.qualified ? 'success' : 'warning'}
          label={
            qualification.signals.lePaidFareP25.status === 'PASS'
              ? 'LE price benchmark passed'
              : 'LE price benchmark not passed'
          }
        />
      </Stack>

      <Box sx={{ display: 'grid', gap: 1.5, gridTemplateColumns: { xs: '1fr', md: 'repeat(3, 1fr)' } }}>
        {Object.entries(qualification.signals).map(([key, signal]) => (
          <Box key={key} sx={{ border: '1px solid', borderColor: 'divider', borderRadius: 2, p: 1.5 }}>
            <Chip
              size="small"
              color={signal.status === 'PASS' ? 'success' : signal.status === 'FAIL' ? 'warning' : 'default'}
              label={signal.status}
              sx={{ mb: 1 }}
            />
            <Typography variant="subtitle2">{SIGNAL_LABELS[key]}</Typography>
            <Typography variant="body2" fontWeight={700} sx={{ my: 0.5 }}>
              Current {money(signal.currentFare, signal.currency)}
              {signal.threshold === null
                ? ' · no comparable benchmark'
                : ` · must be at or below ${money(signal.threshold, signal.currency)}`}
            </Typography>
            <Typography variant="body2" color="text.secondary">
              {signal.reason}
            </Typography>
          </Box>
        ))}
      </Box>

      <Stack direction="row" gap={1} flexWrap="wrap">
        {Object.entries(qualification.hardGates).map(([key, gate]) => (
          <Chip
            key={key}
            variant="outlined"
            color={gate.status === 'PASS' ? 'success' : 'error'}
            label={`${gate.status === 'PASS' ? 'Passed' : 'Failed'} · ${gate.reason}`}
          />
        ))}
      </Stack>
    </Stack>
  );
}
